import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';

import { mq, spacing } from '@/lib/theme';
import type { IReview } from '@/types/content';

import { ReviewCard } from './ReviewCard';

const List = styled.div<{ columns: 1 | 2 }>`
	display: grid;
	gap: ${spacing.space40};
	grid-template-columns: 1fr;

	${mq.lg} {
		grid-template-columns: repeat(
			${({ columns }) => columns},
			minmax(0, 1fr)
		);
	}
`;

interface ReviewListProps {
	reviews: IReview[];
	preview?: boolean;
	onOpen?: (reviewId: number) => void;
	/** Scroll this review into view when the list mounts or the id changes. */
	scrollTo?: number | null;
	columns?: 1 | 2;
}

export const ReviewList = ({
	reviews,
	preview,
	onOpen,
	scrollTo,
	columns = 2,
}: ReviewListProps) => {
	const cards = useRef(new Map<number, HTMLElement>());

	useEffect(() => {
		if (scrollTo != null) {
			cards.current.get(scrollTo)?.scrollIntoView({ block: 'start' });
		}
	}, [scrollTo]);

	return (
		<List columns={columns} data-testid="review-list">
			{reviews.map((review) => (
				<ReviewCard
					key={review.id}
					review={review}
					preview={preview}
					onOpen={onOpen}
					ref={(el) => {
						if (el) cards.current.set(review.id, el);
						else cards.current.delete(review.id);
					}}
				/>
			))}
		</List>
	);
};
